import { useState } from 'react';
import { SERVER_ORIGIN } from '../config';
import { trackEvent } from '../utils/telemetry';

interface AuthUser {
  id: string;
  username: string;
  avatarId: number | null;
  email: string | null;
  emailVerified: boolean;
  bankedResources?: Record<string, number>;
  acceptedTermsVersion?: string | null;
}

type StarterPackKind = 'A' | 'B';
const PACK_OPTIONS: ReadonlyArray<{ kind: StarterPackKind; name: string; desc: string }> = [
  { kind: 'A', name: 'Street Operation', desc: 'Drugs, cops, money, cars. Mainstream gangster kit — two made cards already armed.' },
  { kind: 'B', name: 'The Outfit',        desc: 'Jewels, chips, network, iron. Specialist crew — one made per suit (two on contacts).' },
];

type Mode = 'register' | 'login';

interface RegistrationGateProps {
  onAuth: (token: string, user: AuthUser) => void;
  onMaybeLater: () => void;
  // Riferimento al match tutorial appena giocato: il server verifica la
  // sessione guest e ricalcola il bottino (mai fidarsi di valori dal client).
  tutorialClaim?: {
    tutorialMatchID: string;
    guestToken: string;
  };
}

export function RegistrationGate({ onAuth, onMaybeLater, tutorialClaim }: RegistrationGateProps) {
  const [mode, setMode] = useState<Mode>('register');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [starterPack, setStarterPack] = useState<StarterPackKind>('A');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    if (mode === 'register') {
      trackEvent('funnel_register_click');
    }
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          mode === 'register'
            ? { username, password, email, starterPack, acceptedTerms, ...tutorialClaim }
            : { username, password }
        ),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? `${mode} failed`); return; }
      if (mode === 'register') {
        trackEvent('funnel_register_success', { starterPack, mode: 'tutorial' });
      }
      onAuth(data.token, data.user as AuthUser);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#0c0c18', border: '2px solid var(--accent-color)',
          borderRadius: 10, padding: '28px 28px 20px', maxWidth: 400, width: '92vw',
          color: 'var(--text-color)',
        }}
      >
        {/* Header */}
        <h2 style={{ color: 'var(--accent-color)', margin: '0 0 4px', fontSize: 20 }}>
          {mode === 'register' ? 'Save your progress!' : 'Welcome back!'}
        </h2>
        <p style={{ color: '#999', fontSize: 12, margin: '0 0 18px', lineHeight: 1.5 }}>
          {mode === 'register'
            ? 'Register to unlock Run mode, claim your starter pack and play for keeps.'
            : 'Sign in to access your full account.'}
        </p>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="text" placeholder="Username"
            value={username} onChange={e => setUsername(e.target.value)}
            autoFocus autoComplete="username" style={inputStyle} disabled={busy}
          />
          {mode === 'register' && (
            <input
              type="email" placeholder="Email"
              value={email} onChange={e => setEmail(e.target.value)}
              autoComplete="email" style={inputStyle} disabled={busy} required
            />
          )}
          <input
            type="password" placeholder="Password"
            value={password} onChange={e => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            style={inputStyle} disabled={busy}
          />

          {mode === 'register' && (
            <>
              <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Choose your starter pack</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {PACK_OPTIONS.map(p => {
                  const sel = starterPack === p.kind;
                  return (
                    <button key={p.kind} type="button" onClick={() => setStarterPack(p.kind)} disabled={busy}
                      style={{
                        flex: 1, textAlign: 'left', padding: '7px 9px', fontFamily: 'inherit',
                        background: sel ? '#1a2a3a' : '#15151f', color: '#fff', cursor: busy ? 'wait' : 'pointer',
                        border: `2px solid ${sel ? 'var(--accent-color)' : '#333'}`, borderRadius: 4, fontSize: 11,
                      }}
                    >
                      <div style={{ fontWeight: 'bold', color: sel ? 'var(--accent-color)' : '#ddd', fontSize: 12 }}>{p.name}</div>
                      <div style={{ color: '#888', fontSize: 10, marginTop: 2, lineHeight: 1.4 }}>{p.desc}</div>
                    </button>
                  );
                })}
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: '#bbb', lineHeight: 1.4, cursor: 'pointer' }}>
                <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                  disabled={busy} style={{ marginTop: 2, accentColor: 'var(--accent-color)' }} />
                <span>
                  I am at least 14 and I accept the{' '}
                  <a href="/legal/terms" target="_blank" rel="noopener" style={{ color: '#4fc3f7' }}>Terms of Service</a> and{' '}
                  <a href="/legal/privacy" target="_blank" rel="noopener" style={{ color: '#4fc3f7' }}>Privacy Policy</a>.
                </span>
              </label>
            </>
          )}

          {error && <div style={{ color: '#e57373', fontSize: 12, textAlign: 'center' }}>{error}</div>}

          <button
            type="submit"
            className="multi-btn"
            disabled={busy || !username || !password || (mode === 'register' && (!email || !acceptedTerms))}
            style={{ marginTop: 4 }}
          >
            {busy ? '...' : mode === 'register' ? 'Register & save progress' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
          {mode === 'register' ? (
            <button onClick={() => { setMode('login'); setError(null); }} style={linkStyle}>
              Already have an account? Sign in
            </button>
          ) : (
            <button onClick={() => { setMode('register'); setError(null); }} style={linkStyle}>
              No account yet? Register
            </button>
          )}
          <button onClick={onMaybeLater} style={{ ...linkStyle, color: '#666', fontSize: 11 }}>
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '9px 11px', fontSize: 13, fontFamily: 'inherit',
  background: '#1a1a24', border: '1px solid #444', borderRadius: 4, color: 'var(--text-color)',
};

const linkStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#4fc3f7',
  cursor: 'pointer', textDecoration: 'underline', fontSize: 12,
  fontFamily: 'inherit', padding: 0,
};
